import { revalidateLogic, useForm } from '@tanstack/react-form'
import { crearFacturaManualSchema, DEFAULT_FACTURA_MANUAL, type FacturaManualFormValues } from './facturaManualSchema'

interface UseFacturaManualFormOptions {
  onSubmit: (values: FacturaManualFormValues) => void | Promise<void>
  /** CUIT del emisor del estudio. Sin él, el servidor igual rechaza facturarse a sí mismo. */
  cuitEmisor?: string | null
}

/** `['lineas', 0, 'importe']` → `lineas[0].importe`, el nombre del campo en TanStack Form. */
export const nombreDeCampo = (path: readonly PropertyKey[]) =>
  path.reduce<string>((acc, p) => (typeof p === 'number' ? `${acc}[${p}]` : acc ? `${acc}.${String(p)}` : String(p)), '')

/** Primer error de cada campo, con el nombre que usa TanStack Form (incluidas las líneas). */
export const erroresPorCampo = (issues: ReadonlyArray<{ path: readonly PropertyKey[]; message: string }>) => {
  const campos: Record<string, string> = {}
  for (const issue of issues) {
    const nombre = nombreDeCampo(issue.path)
    if (nombre && !(nombre in campos)) campos[nombre] = issue.message
  }
  return campos
}

export function useFacturaManualForm({ onSubmit, cuitEmisor }: UseFacturaManualFormOptions) {
  const schema = crearFacturaManualSchema(cuitEmisor)
  const form = useForm({
    defaultValues: DEFAULT_FACTURA_MANUAL,
    // Valida al enviar y, después del primer intento, en cada cambio, así un error entre campos se limpia al corregir el otro campo.
    validationLogic: revalidateLogic(),
    validators: {
      // Validador manual, igual que GastoForm, para que cada issue de Zod caiga en su campo.
      onDynamic: ({ value }) => {
        const result = schema.safeParse(value)
        if (result.success) return undefined
        return { fields: erroresPorCampo(result.error.issues) }
      },
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value)
    },
  })
  return { form }
}

export type UseFacturaManualFormReturn = ReturnType<typeof useFacturaManualForm>
export type FacturaManualForm = UseFacturaManualFormReturn['form']
